'use client';

import {
	BigNum,
	decodeName,
	SpotMarketConfig,
	User,
	UserAccount,
} from '@drift-labs/sdk';
import {
	COMMON_UI_UTILS,
	MarketId,
	JLP_POOL_ID,
	createWithdrawIx,
} from '@drift/common';
import { useCallback } from 'react';
import { ComputeUnits } from 'src/@types/types';
import { isAmplifyAccountName } from 'src/utils/amplify';
import captureException from 'src/utils/captureException';
import { notify } from 'src/utils/notifications';
import TransactionErrorHandler from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import { getOracleCrankIxs } from 'src/utils/oracle';
import { updateAfterTx } from 'src/stores/DriftStore/helpers';
import useDriftAccountsStore from 'src/stores/useDriftAccountsStore';
import { accountTradingDataQueryKeys } from 'src/hooks/query/accountTradingData/accountTradingDataQueryKeys';
import { PositionsByUserKey } from 'src/hooks/query/accountTradingData/utils/types';
import useCurrentSlotRef from 'src/hooks/useCurrentSlotRef';
import { useGetTxParams } from 'src/hooks/driftActions/transactions/useGetTxParams';
import { JLP_POOL_JLP_MARKET_INDEX } from 'src/environmentVariables/EnvironmentVariables';
import useDriftClient from '../../useDriftClient';
import invariant from 'tiny-invariant';
import useCurrentUserAccount from '../../useCurrentUserAccount';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useSponsoredSignDriftActionWithFallback } from 'src/hooks/wallets/useSponsoredSignDriftActionWithFallback';
import { useQueryClient } from '@tanstack/react-query';
import type { WalletName } from '@solana/wallet-adapter-base';

interface WithdrawCollateralParams {
	amount: BigNum;
	spotMarketConfig: SpotMarketConfig;
	userAccount?: UserAccount;
	isBorrow?: boolean;
	isMax?: boolean;
	updateFuel?: boolean;
}

export function useWithdrawCollateral() {
	const driftClient = useDriftClient();
	const currentUserAccount = useCurrentUserAccount();
	const walletAdapter = useDriftStore((s) => s.wallet.current.adapter);
	const currentSlotRef = useCurrentSlotRef();

	const accountsGet = useDriftAccountsStore((s) => s.get);
	const queryClient = useQueryClient();
	const getTxParams = useGetTxParams();

	const withdrawAction = useCallback(
		async (
			wallet: UiEnhancedIWalletV2,
			userClient: User,
			amount: BigNum,
			spotMarketConfig: SpotMarketConfig,
			isBorrow: boolean,
			isMax: boolean,
			allActiveMarketIds: MarketId[],
			walletName: WalletName<string>,
			currentSlot: number
		) => {
			invariant(driftClient, 'driftClient is required');

			const optionalIxs = await getOracleCrankIxs(
				allActiveMarketIds,
				driftClient,
				walletName,
				{ current: currentSlot },
				undefined,
				2
			);

			const withdrawIxs = await createWithdrawIx({
				driftClient,
				user: userClient,
				amount: amount.shiftTo(spotMarketConfig.precisionExp),
				spotMarketConfig,
				isBorrow,
				isMax,
			});

			const allIxs = [...optionalIxs, ...withdrawIxs];

			const txParams = getTxParams({
				marketId: MarketId.createSpotMarket(spotMarketConfig.marketIndex),
				computeUnits: 1_200_000 as ComputeUnits,
				lowerBoundCu: 450_000,
			});

			await wallet.signAndSendTransaction(allIxs, txParams);
		},
		[driftClient, getTxParams]
	);

	const wrappedWithdraw =
		useSponsoredSignDriftActionWithFallback(withdrawAction);

	const withdrawCollateral = useCallback(
		async ({
			amount,
			spotMarketConfig,
			userAccount,
			isBorrow = false,
			isMax = false,
		}: WithdrawCollateralParams) => {
			invariant(driftClient, 'Failed to get drift client');

			if (!userAccount) {
				userAccount = currentUserAccount?.client?.getUserAccount();
			}

			// can't withdraw from a delegate account

			const walletPubkey = walletAdapter?.publicKey;
			if (
				!driftClient ||
				!walletAdapter ||
				!walletPubkey ||
				!userAccount ||
				!userAccount.authority.equals(walletPubkey)
			)
				return;

			const toastId = `${
				isBorrow ? 'borrow' : 'withdraw'
			}-${amount.toString()}-${Date.now()}`;

			// cant open a borrow on the deposit asset in an amplify account
			if (
				userAccount.poolId === JLP_POOL_ID &&
				isAmplifyAccountName(decodeName(userAccount.name))
			) {
				if (
					spotMarketConfig.marketIndex === JLP_POOL_JLP_MARKET_INDEX &&
					isBorrow
				) {
					notify({
						type: 'error',
						message: 'Invalid Amplify Position',
						id: toastId,
					});
					return false;
				}
			}

			await driftClient.switchActiveUser(
				userAccount.subAccountId,
				userAccount.authority
			);

			notify({
				type: 'awaiting_wallet_confirmation',
				id: toastId,
				showUntilCancelled: true,
			});

			const userKey = COMMON_UI_UTILS.getUserKey(
				userAccount.subAccountId,
				userAccount.authority
			);
			const accountData = accountsGet().accounts[userKey];

			const positionsData = queryClient.getQueryData<PositionsByUserKey>(
				accountTradingDataQueryKeys.positions()
			);
			const perpMarketIds = (
				positionsData?.[userKey]?.openPerpPositions ?? []
			).map((position) => MarketId.createPerpMarket(position.marketIndex));

			const spotMarketIds =
				accountData?.spotBalances.map((balance) =>
					MarketId.createSpotMarket(balance.asset.marketIndex)
				) ?? [];

			const allActiveMarketIds = [...perpMarketIds, ...spotMarketIds];

			const walletName = walletAdapter?.name;
			const userClient = accountsGet().accounts[userKey]?.client;

			if (!walletName || !userClient) {
				throw new Error('Missing required dependencies for withdrawal');
			}

			try {
				await wrappedWithdraw(
					userClient,
					amount,
					spotMarketConfig,
					isBorrow,
					isMax,
					allActiveMarketIds,
					walletName,
					currentSlotRef.current
				);

				driftClient.spotMarketLastSlotCache.set(
					spotMarketConfig.marketIndex,
					currentSlotRef.current
				);

				notify({
					type: 'success',
					message: `${isBorrow ? 'Borrow' : 'Withdraw'} Successful.`,
					id: toastId,
					updatePrevious: true,
				});

				const userClientForUpdate = accountData?.client;

				if (!userClientForUpdate) throw new Error('Failed to get user client');

				updateAfterTx(driftClient, userClientForUpdate);

				return true;
			} catch (e: any) {
				console.error(e);
				captureException(e);
				TransactionErrorHandler.handleError({
					error: e,
					toastId,
					wallet: walletAdapter,
				});
				return false;
			}
		},
		[
			driftClient,
			walletAdapter,
			currentUserAccount,
			accountsGet,
			wrappedWithdraw,
		]
	);

	return { withdrawCollateral };
}
